import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, switchMap, throwError } from "rxjs";
import { environment } from "../../environments/environment";
import { SessionRefreshService } from "../auth/data-access/session-refresh.service";
import { clearSession } from "../shared/utils/session.util";

/** Auth endpoints never trigger a refresh: their 401 is a real answer. */
const NO_REFRESH_PATHS = [
  environment.SERVICES.LOGIN,
  environment.SERVICES.LOGOUT,
  environment.SERVICES.REFRESH,
  environment.SERVICES.PASSWORD_RECOVERY_REQUEST,
  environment.SERVICES.PASSWORD_RECOVERY_VERIFY,
  environment.SERVICES.PASSWORD_RECOVERY_RESET,
].map((path) => environment.API_DOMAIN + path);
const LOGIN_URL = environment.API_DOMAIN + environment.SERVICES.LOGIN;

const withToken = (req: HttpRequest<unknown>, token: string | null) =>
  token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

/**
 * Adds the access token. On a 401 from a protected endpoint it renews the
 * session once with the refresh token and retries the request; when the
 * renewal fails the session is cleared and the user goes to the login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const sessionRefresh = inject(SessionRefreshService);

  const endSession = (error: unknown) => {
    clearSession();
    void router.navigate(["/login"]);
    return throwError(() => error);
  };

  return next(withToken(req, localStorage.getItem("token"))).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }
      const canRefresh =
        !NO_REFRESH_PATHS.includes(req.url) && !!localStorage.getItem("refreshToken");
      if (!canRefresh) {
        // A 401 on login is a wrong password, not an expired session.
        return req.url === LOGIN_URL ? throwError(() => error) : endSession(error);
      }
      return sessionRefresh.refresh().pipe(
        catchError(() => endSession(error)),
        switchMap((token) =>
          next(withToken(req, token)).pipe(
            // Still 401 with a fresh token: the session is gone (e.g. revoked).
            catchError((retryError: HttpErrorResponse) =>
              retryError.status === 401 ? endSession(retryError) : throwError(() => retryError)
            )
          )
        ),
      );
    })
  );
};
